import { AppCard } from "@lib/components/AppComponents";
import { Empty, Skeleton, Typography, Badge, Row, Col } from "antd";
import { UseQueryProps } from "@lib/types";
import { XAICallLogsDashboardResponse } from "@lib/types/xAI";
import { useMemo } from "react";
import { FaRobot } from "react-icons/fa";
import styled from "styled-components";

type Props = {
  query?: UseQueryProps<XAICallLogsDashboardResponse>;
  extra?: string | JSX.Element;
  loading?: boolean;
  refetch?: () => void;
};

const StyledMonitoringCard = styled.div<{ isAlert?: boolean }>`
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 12px;
  padding: 10px 20px;
  border: 1px solid
    ${({ theme, isAlert }) =>
      isAlert ? "rgba(255, 77, 79, 0.3)" : `${theme.palette.borderColor}`};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border-color: ${({ theme, isAlert }) =>
      isAlert ? "rgba(255, 77, 79, 0.5)" : `${theme.palette.borderColor}`};
  }
`;

const StatusBadge = styled(Badge)<{ status: string }>`
  .ant-badge-status-dot {
    width: 10px;
    height: 10px;
  }

  ${({ status }) => {
    switch (status) {
      case "KONUŞUYOR":
        return "color: #52c41a;";
      case "BEKLİYOR":
        return "color: #faad14;";
      case "IDLE":
        return "color: #8c8c8c;";
      case "UYARI":
        return "color: #ff4d4f;";
      default:
        return "color: #8c8c8c;";
    }
  }}
`;

const RobotIcon = styled.div<{ isAlert?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${({ theme, isAlert }) =>
    isAlert
      ? "linear-gradient(135deg, rgba(255, 77, 79, 0.1) 0%, rgba(255, 77, 79, 0.05) 100%)"
      : `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}08 100%)`};
  border: ${({ isAlert }) =>
    isAlert ? "2px solid rgba(255, 77, 79, 0.3)" : "none"};
  transition: all 0.3s ease;

  svg {
    font-size: 28px;
    color: ${({ isAlert, theme }) =>
      isAlert ? "#ff4d4f" : theme.palette.primary.main};
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }
`;

const KPICard = styled.div`
  text-align: center;
  padding: 16px 24px;
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.palette.primary.main} 0%,
      ${({ theme }) =>
          theme.palette.secondary?.main || theme.palette.primary.main}
        100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 0, 0, 0.1);

    &::before {
      opacity: 1;
    }
  }
`;

const KPINumber = styled(Typography.Title)`
  margin: 0 !important;
  font-size: 42px !important;
  font-weight: 700 !important;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.palette.primary.main} 0%,
    ${({ theme }) =>
        theme.palette.secondary?.main || theme.palette.primary.main}
      100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -1px;
`;

const KPILabel = styled(Typography.Text)`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.text.secondary};
  margin-top: 12px;
  display: block;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

// Demo data generator
const generateDemoMonitoringData = () => {
  const companies = [
    "GLOBAL MARKETS FX",
    "PRIME TRADING CO.",
    "BLUE CHIP CAPITAL",
    "EURASIA BANK",
  ];

  const statuses = ["KONUŞUYOR", "BEKLİYOR", "IDLE", "UYARI"];

  return Array.from({ length: 6 }, (_, i) => {
    const status = statuses[i % statuses.length];
    const duration =
      status === "KONUŞUYOR"
        ? `${Math.floor(Math.random() * 10) + 1}M ${Math.floor(Math.random() * 60)}S`
        : null;

    return {
      id: `monitor-${i + 1}`,
      companyName: companies[i % companies.length],
      status,
      duration,
      isAlert: status === "UYARI",
    };
  });
};

const TftSalesLiveMonitoringCenter = (props: Props) => {
  const { query, extra, loading } = props;
  const { data, isLoading, isFetching } = query || {};

  // Try to get real data from API, fallback to demo
  const monitoringData = useMemo(() => {
    // TODO: When API supports this, use real data
    // For now, use demo data
    return generateDemoMonitoringData();
  }, [data]);

  // Calculate KPIs from demo data (or real data when available)
  const kpis = useMemo(() => {
    const activeAgents = monitoringData.filter(
      (item) => item.status === "KONUŞUYOR",
    ).length;
    const queueCount = monitoringData.filter(
      (item) => item.status === "BEKLİYOR",
    ).length;
    const systemHealth = 99.8; // Demo value

    return {
      activeAgents,
      queueCount,
      systemHealth,
    };
  }, [monitoringData]);

  return (
    <AppCard
      title="Canlı İzleme Merkezi"
      extra={extra}
      style={{ marginBottom: 16 }}
    >
      {isLoading || isFetching || loading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <div style={{ padding: "16px" }}>
          {/* KPI Section */}
          <Row gutter={[20, 10]} style={{ marginBottom: 10 }}>
            <Col xs={24} sm={8}>
              <KPICard>
                <KPINumber level={1}>{kpis.activeAgents}</KPINumber>
                <KPILabel>AKTIF AGENTLAR</KPILabel>
              </KPICard>
            </Col>
            <Col xs={24} sm={8}>
              <KPICard>
                <KPINumber level={1}>{kpis.queueCount}</KPINumber>
                <KPILabel>KUYRUK</KPILabel>
              </KPICard>
            </Col>
            <Col xs={24} sm={8}>
              <KPICard>
                <KPINumber level={1}>{kpis.systemHealth}%</KPINumber>
                <KPILabel>SISTEM SAĞLIĞI</KPILabel>
              </KPICard>
            </Col>
          </Row>

          {/* Monitoring Cards */}
          {monitoringData.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <Empty />
            </div>
          ) : (
            <Row gutter={[20, 5]}>
              {monitoringData.map((item) => (
                <Col xs={24} sm={12} lg={8} key={item.id}>
                  <StyledMonitoringCard isAlert={item.isAlert}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 16 }}
                    >
                      <RobotIcon isAlert={item.isAlert}>
                        <FaRobot />
                      </RobotIcon>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 10 }}>
                          <Typography.Text
                            type="secondary"
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              letterSpacing: "0.5px",
                              textTransform: "uppercase",
                            }}
                          >
                            {item.companyName}
                          </Typography.Text>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <StatusBadge
                            status={item.status}
                            text={
                              <Typography.Text
                                style={{
                                  color:
                                    item.status === "KONUŞUYOR"
                                      ? "#1890ff"
                                      : item.status === "UYARI"
                                        ? "#ff4d4f"
                                        : "#8c8c8c",
                                  fontWeight: 600,
                                  fontSize: 13,
                                }}
                              >
                                {item.status}
                              </Typography.Text>
                            }
                          />
                        </div>
                        {item.duration && (
                          <Typography.Text
                            style={{
                              color: "#1890ff",
                              fontSize: 11,
                              fontWeight: 500,
                              marginTop: 4,
                              display: "block",
                            }}
                          >
                            {item.duration}
                          </Typography.Text>
                        )}
                        {item.isAlert && (
                          <Typography.Text
                            style={{
                              color: "#ff4d4f",
                              fontSize: 11,
                              fontWeight: 600,
                              marginTop: 4,
                              display: "block",
                            }}
                          >
                            ALERT
                          </Typography.Text>
                        )}
                      </div>
                    </div>
                  </StyledMonitoringCard>
                </Col>
              ))}
            </Row>
          )}
        </div>
      )}
    </AppCard>
  );
};

export default TftSalesLiveMonitoringCenter;
